/**
 * MainNav
 * @module components/menu/main_nav
 */

import React, { createElement, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, NavLink } from "../../interfaces/router";
import { fetchMenu } from "../../actions/menu.actions";

import ActivityBar from "../framework/activity-bar.js";

import { useRestrictAccess } from "@/hooks/subscriptions/useRestrictAccess";
import { useUserCompanies } from "../../hooks/companies/useUserCompanies.js";
import CompanyAvatar from "@/components/companyAvatar";

import {
  BookOpen,
  Building2,
  ChartPie,
  Circle,
  CreditCard,
  Files,
  Folder,
  Gauge,
  ListChecks,
  ListPlus,
  Mail,
  Megaphone,
  Menu,
  Presentation,
  ReceiptText,
  Search,
  Settings2,
  Sparkles,
  TrendingUp,
  UserCog,
  Users,
  X,
} from "lucide-react";
import { t } from "@/i18n";
import { cn } from "../utils";
import { Badge } from "../ui/badge";
import { useHistory } from "react-router-dom";
import { Skeleton } from "../ui/skeleton";
import LoadingPanel from "../loadingPanel";
import { ROUTES } from "@/constants/routes";
import { Button } from "../ui/button";

// Icon names are sent by the resources service: server/data.js and
// server/middlewares/check-if-shares-is-initialized.js. Importing lucide's whole
// icons object instead would put every icon in the main bundle.
const MENU_ICONS = {
  BookOpen,
  Building2,
  ChartPie,
  CreditCard,
  Files,
  Folder,
  Gauge,
  ListChecks,
  ListPlus,
  Mail,
  Megaphone,
  Presentation,
  ReceiptText,
  Search,
  Settings2,
  Sparkles,
  TrendingUp,
  UserCog,
  Users,
};

const RESERVED_ROUTES = Object.values(ROUTES).map((route) =>
  route.substring(1),
);
function MainNav({ userResources, company, companyResources, fetchMenu }) {
  const [isOpen, setIsOpen] = useState(false);
  const history = useHistory();
  const currentUrlAlias = history?.location?.pathname?.split("/")?.[1];
  const isLoadingCompany =
    company &&
    company?.urlAlias !== currentUrlAlias &&
    !RESERVED_ROUTES.includes(currentUrlAlias);

  const { restrict, restrictDialogElement } = useRestrictAccess(company?.id);
  const { data: userCompaniesQuery } = useUserCompanies("me");

  const loadMenu = () => {
    fetchMenu("main");
  };

  useEffect(() => {
    loadMenu();
  }, []);

  const activeCompanyMenuRef = useRef(null);

  const renderMenu = (item) => {
    const topLevel = item.level === 0;

    const icon = topLevel
      ? null
      : createElement(MENU_ICONS[item.icon] ?? Circle, {
          className: !item.icon ? "invisible" : "",
          size: 14,
        });

    const applyRestriction = (element) =>
      item.restrict
        ? restrict(element, { requiredFeature: item.restrict })
        : element;

    return (
      <div className="flex flex-col">
        {topLevel ? (
          <div className="mt-3 pb-3 text-sm text-slate-500 uppercase">
            {t(item.translationId)}
          </div>
        ) : (
          applyRestriction(
            <NavLink
              onClick={() => setIsOpen(false)}
              to={item.url}
              activeClassName={cn({
                "!text-slate-50 bg-brand-800/50 hover:bg-brand-800/50":
                  !topLevel,
              })}
              className={cn(
                "text-md flex items-center gap-3 rounded-md p-3 font-semibold text-slate-300",
                { "hover:bg-brand-800/10 hover:text-slate-50": !topLevel },
              )}
            >
              {icon} {t(item.translationId)}{" "}
              {item.isNewFeature && (
                <Badge
                  size="sm"
                  className="text-primary p-0 px-2 font-bold"
                  variant="outline-primary"
                >
                  {t("generic.new")}
                </Badge>
              )}
            </NavLink>,
          )
        )}

        {item.children?.map?.(renderMenu)}
      </div>
    );
  };

  const hasCompanyResources = companyResources?.length > 0;

  const sortedUserCompanies = userCompaniesQuery?.data
    ?.toSorted?.((a, b) => {
      return a.id === company?.id ? -1 : b.id === company?.id ? 1 : 0;
    })
    ?.filter((c) => !c.isCanceled);

  const selectedCompany = company;

  return (
    <>
      <div
        className={cn("fixed top-[2px] left-[10px] z-100", {
          invisible: isOpen,
        })}
      >
        <Button
          className="rounded-full lg:hidden"
          variant="ghost"
          onClick={() => setIsOpen(true)}
        >
          <Menu />
        </Button>
      </div>
      <div
        className={cn(
          "fixed z-100 flex grid h-[100vh] w-[100vw] grid-cols-[240px_1fr] overflow-hidden",
          "lg:pointer-events-auto lg:static lg:m-0 lg:w-[240px]",
          {
            "pointer-events-none": !isOpen,
          },
        )}
      >
        <nav
          className={cn(
            "relative z-[100] h-[100vh] w-full flex-col bg-[#1F3239] shadow-lg",
            "lg:pointer-events-auto lg:visible! lg:flex! lg:shadow-none",
            {
              invisible: !isOpen,
            },
          )}
        >
          <>
            <Link
              to="/"
              className="mt-2 mb-4 flex min-h-[40px] overflow-hidden"
            >
              <ActivityBar />
            </Link>
            <Button
              variant="ghost"
              className="text-brand-200 absolute top-2 right-2 rounded-full lg:hidden"
              onClick={() => setIsOpen(false)}
            >
              <X />
            </Button>
          </>
          <div className="flex h-full flex-col gap-4 overflow-y-auto px-4 lg:overflow-y-hidden">
            <div className="flex flex-col gap-2 rounded-xl pb-4">
              {userResources?.map?.(renderMenu)}
            </div>
            <div className="flex h-full flex-col gap-3 overflow-y-auto pb-[14rem] lg:pb-[12rem]">
              {sortedUserCompanies?.map?.((companyItem) => {
                const isActive = selectedCompany?.id === companyItem.id;
                if (isLoadingCompany && isActive) {
                  return (
                    <div
                      className="relative"
                      key={`skeleton-${companyItem.id}`}
                      style={{
                        height: `${activeCompanyMenuRef.current?.clientHeight}px`,
                      }}
                    >
                      <Skeleton
                        className={cn(
                          "border-brand-800 bg-brand-800/50 h-full w-full border",
                        )}
                      />
                      <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
                        <LoadingPanel />
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={companyItem.id}
                    ref={isActive ? activeCompanyMenuRef : null}
                  >
                    <div
                      className={cn(
                        "group rounded-xl border border-slate-700 shadow-lg transition-colors",
                        {
                          "bg-brand-800/30": isActive,
                          "border-2 border-orange-900": !companyItem.verified,
                        },
                      )}
                    >
                      <Link
                        to={`/${companyItem.urlAlias}`}
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="flex cursor-pointer items-center gap-4 p-2 font-semibold text-slate-500">
                          <CompanyAvatar
                            companyId={companyItem.id}
                            companyName={companyItem.name}
                          />
                          <div
                            className={cn("w-full group-hover:text-slate-100", {
                              "text-slate-100": isActive,
                              "relative min-h-[46px]": !companyItem.verified,
                            })}
                          >
                            {companyItem.name}
                            {!companyItem.verified && (
                              <Badge
                                title={t(
                                  "support_widget.modal.actions.contact_support",
                                )}
                                variant="warning"
                                className="absolute right-0 bottom-0"
                              >
                                {t("menu.company.title.btn.unverified")}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </Link>
                      {isActive && (
                        <div className="mt-1 border-t border-slate-700" />
                      )}
                      <div className="px-2">
                        {isActive &&
                          hasCompanyResources &&
                          companyResources.map(renderMenu)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {restrictDialogElement}
        </nav>
        <div
          onClick={() => setIsOpen(false)}
          // backdrop
          className={cn("frosted bg-slate-500/50 lg:hidden", {
            hidden: !isOpen,
          })}
        ></div>
      </div>
    </>
  );
}

function mapStateToProps(state) {
  return {
    userResources: state.menu.userResources,
    companyResources: state.menu.companyResources,
    company: state.company.company,
  };
}

const mapDispatchToProps = {
  fetchMenu,
};

export { MainNav };
export default connect(mapStateToProps, mapDispatchToProps)(MainNav);
